//EventLog.jsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select,
    Button,
    Flex,
    Heading,
    Card,
    useToast,
    Icon,
    Input,
    Spinner,
    Text,
    Badge
} from "@chakra-ui/react";
import { BellIcon, DownloadIcon, SearchIcon } from "@chakra-ui/icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import axios from "axios";

// Custom input component for DatePicker with proper styling
const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <Input
        value={value}
        ref={ref}
        onClick={onClick}
        readOnly
        cursor="pointer"
        border="1px solid"
        borderColor="gray.300"
        _hover={{ borderColor: "blue.400" }}
        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
    />
));

export function EventLog() {
    const [tArray, setTArray] = useState([]);
    const [filteredArray, setFilteredArray] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [retry, setRetry] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [filteredPageCount, setFilteredPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedType, setSelectedType] = useState('0');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [isSearching, setIsSearching] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [searchResultCount, setSearchResultCount] = useState(0);
    const toast = useToast();

    const itemsPerPage = 10;

    const handleSelectedType = (event) => {
        setSelectedType(event.target.value);
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    // Get total record count on initial load
    useEffect(() => {
        const httpClient = axios.create({
            baseURL: 'https://villarhapsodyiot.org',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        const getData = async () => {
            try {
                const connectionPath = '/api/villarhapastrecord/1/10/';
                const response = await httpClient.get(connectionPath);

                let mArray = response.data.map((item) => ({
                    Id: item.item,
                    Label: item.label,
                    Status: item.status,
                    DataType: item.dataType,
                    Timestamp: item.timestamp,
                    Acknowledge: item.acknowledge,
                    Name: item.name
                }));

                setTArray(mArray);
            } catch (error) {
                console.error(error);
                setRetry(true);
                toast({
                    title: "Error fetching data",
                    description: "Failed to load event log data",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        };

        const getCount = async () => {
            try {
                const connectionPath = '/api/villarhapastrecord/count';
                const response = await httpClient.get(connectionPath);
                setPageCount(response.data);
            } catch (error) {
                console.error(error);
                setRetry(true);
                toast({
                    title: "Error fetching count",
                    description: "Failed to load page count",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        };

        getCount();
        getData();
    }, []);

    // Get paginated data when currentPage changes (for unfiltered data)
    useEffect(() => {
        if (isFiltered) return;

        const httpClient = axios.create({
            baseURL: 'https://villarhapsodyiot.org',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        const getData = async () => {
            try {
                const connectionPath = '/api/villarhapastrecord/' + currentPage.toString() + '/10/';
                const response = await httpClient.get(connectionPath);

                let mArray = response.data.map((item) => ({
                    Id: item.item,
                    Label: item.label,
                    Status: item.status,
                    DataType: item.dataType,
                    Timestamp: item.timestamp,
                    Acknowledge: item.acknowledge,
                    Name: item.name
                }));

                setTArray(mArray);
            } catch (error) {
                console.error(error);
                setRetry(true);
                toast({
                    title: "Error fetching page data",
                    description: "Failed to load page " + currentPage,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        };

        getData();
    }, [currentPage, isFiltered]);

    // Handle filtered data pagination
    useEffect(() => {
        if (!isFiltered || filteredArray.length === 0) return;

        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedData = filteredArray.slice(startIndex, endIndex);

        setTArray(paginatedData);
    }, [currentPage, filteredArray, isFiltered]);

    const handleSearchClick = async () => {
        setIsSearching(true);
        try {
            const response = await fetch('https://villarhapsodyiot.org/api/villarhapastrecord/filter/', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json;charset=UTF-8'
                },
                body: JSON.stringify({
                    type: selectedType,
                    startdate: startDate.toISOString(),
                    enddate: endDate.toISOString(),
                })
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();

            let mArray = data.map((item) => ({
                Id: item.item,
                Label: item.label,
                Status: item.status,
                DataType: item.dataType,
                Timestamp: item.timestamp,
                Acknowledge: item.acknowledge,
                Name: item.name
            }));

            // Set filtered data
            setFilteredArray(mArray);
            setFilteredPageCount(Math.ceil(mArray.length / itemsPerPage));
            setSearchResultCount(mArray.length);
            setIsFiltered(true);
            setCurrentPage(1);

            toast({
                title: "Search successful",
                description: `Found ${mArray.length} records`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            console.error('Error:', error);
            toast({
                title: "Search failed",
                description: "An error occurred during search",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSearching(false);
        }
    };

    const handleExportClick = async () => {
        if (!isFiltered) {
            toast({
                title: "No search results",
                description: "Please perform a search first before exporting",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsExporting(true);
        try {
            const response = await fetch('https://villarhapsodyiot.org/api/villarhapastrecord/filter/', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json;charset=UTF-8'
                },
                body: JSON.stringify({
                    type: selectedType,
                    startdate: startDate.toISOString(),
                    enddate: endDate.toISOString(),
                })
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();

            // Format data for export
            const exportData = data.map((item) => ({
                'Alert ID': item.item,
                'Alert Name': item.label,
                'Alert Status': item.status,
                'Created Time': new Date(item.timestamp).toLocaleString('zh-HK')
            }));

            // Export to Excel
            exportToExcel(exportData, 'EventLogReport');

            toast({
                title: "Export successful",
                description: `Exported ${exportData.length} records to Excel`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            console.error('Error:', error);
            toast({
                title: "Export failed",
                description: "An error occurred during export",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsExporting(false);
        }
    };

    const handleClearFilter = () => {
        setIsFiltered(false);
        setFilteredArray([]);
        setSearchResultCount(0);
        setCurrentPage(1);

        // Reset to get first page of unfiltered data
        const httpClient = axios.create({
            baseURL: 'https://villarhapsodyiot.org',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        const getData = async () => {
            try {
                const connectionPath = '/api/villarhapastrecord/1/10/';
                const response = await httpClient.get(connectionPath);

                let mArray = response.data.map((item) => ({
                    Id: item.item,
                    Label: item.label,
                    Status: item.status,
                    DataType: item.dataType,
                    Timestamp: item.timestamp,
                    Acknowledge: item.acknowledge,
                    Name: item.name
                }));

                setTArray(mArray);
            } catch (error) {
                console.error(error);
                toast({
                    title: "Error fetching data",
                    description: "Failed to load event log data",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        };

        getData();
    };

    const exportToExcel = (data, fileName) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Event Log');

        // Auto-size columns
        const wscols = [
            { wch: 10 }, // Alert ID
            { wch: 40 }, // Alert Name
            { wch: 25 }, // Alert Status
            { wch: 25 }, // Created Time
        ];
        worksheet['!cols'] = wscols;

        // Add current date to filename
        const dateStr = new Date().toISOString().split('T')[0];
        XLSX.writeFile(workbook, `${fileName}_${dateStr}.xlsx`);
    };

    const Pagination = () => {
        const currentPageCount = isFiltered ? filteredPageCount : Math.ceil(pageCount / itemsPerPage);
        const maxPage = currentPageCount;

        const getPaginationGroup = () => {
            let start = Math.floor((currentPage - 1) / 5) * 5;
            return new Array(5).fill().map((_, idx) => start + idx + 1);
        };

        return (
            <Flex justify="space-between" mt={4} alignItems="center">
                <Flex alignItems="center">
                    {isFiltered ? (
                        <>
                            <Text fontSize="md" fontWeight="medium" mr={2}>
                                Found {searchResultCount} records
                            </Text>
                            <Button
                                onClick={handleClearFilter}
                                colorScheme="gray"
                                size="sm"
                                variant="outline"
                            >
                                Clear Search
                            </Button>
                        </>
                    ) : (
                        <Text fontSize="md" color="gray.600">
                            Showing all records
                        </Text>
                    )}
                </Flex>

                <Flex alignItems="center">
                    <Button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        mr={2}
                        size="sm"
                    >
                        Prev
                    </Button>

                    {getPaginationGroup().map((item, index) => (
                        item <= maxPage && (
                            <Button
                                key={index}
                                onClick={() => setCurrentPage(item)}
                                disabled={item > maxPage}
                                colorScheme={item === currentPage ? "blue" : "gray"}
                                mx={1}
                                size="sm"
                                variant={item === currentPage ? "solid" : "outline"}
                            >
                                {item}
                            </Button>
                        )
                    ))}

                    <Text mx={2} fontSize="md">
                        Page {currentPage} of {maxPage}
                    </Text>

                    <Button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, maxPage))}
                        disabled={currentPage === maxPage}
                        ml={2}
                        size="sm"
                    >
                        Next
                    </Button>
                </Flex>
            </Flex>
        );
    };

    return (
        <Box m="20px">
            <Card p={4} mb={4}>
                <Flex align="center" mb={4}>
                    <Icon as={BellIcon} boxSize={6} mr={2} />
                    <Heading size="lg">Event Log</Heading>
                </Flex>

                <Flex direction={["column", "column", "row"]} mb={4} gap={4} alignItems="flex-end">
                    <Box flex="1">
                        <Box mb={2} fontWeight="medium" fontSize="md">Alert Type</Box>
                        <Select
                            value={selectedType}
                            onChange={handleSelectedType}
                            size="md"
                            fontSize="md"
                        >
                            <option value="0">所有警報</option>
                            <option value="1">升降機警報</option>
                            <option value="2">消防警報</option>
                            <option value="3">住戶警報</option>
                            <option value="4">泵房控制箱警報</option>
                        </Select>
                    </Box>

                    <Box flex="1">
                        <Box mb={2} fontWeight="medium" fontSize="md">Start Time</Box>
                        <DatePicker
                            selected={startDate}
                            onChange={handleStartDateChange}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="dd-MM-yyyy HH:mm"
                            timeCaption="Time"
                            customInput={<CustomDateInput />}
                            maxDate={new Date()}
                        />
                    </Box>

                    <Box flex="1">
                        <Box mb={2} fontWeight="medium" fontSize="md">End Time</Box>
                        <DatePicker
                            selected={endDate}
                            onChange={handleEndDateChange}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="dd-MM-yyyy HH:mm"
                            timeCaption="Time"
                            customInput={<CustomDateInput />}
                            minDate={startDate}
                            maxDate={new Date()}
                        />
                    </Box>

                    <Flex flex="1" gap={3}>
                        <Button
                            colorScheme="blue"
                            onClick={handleSearchClick}
                            leftIcon={isSearching ? <Spinner size="sm" /> : <SearchIcon />}
                            width="50%"
                            isLoading={isSearching}
                            loadingText="Searching"
                            fontSize="md"
                            height="42px"
                        >
                            Search
                        </Button>

                        <Button
                            colorScheme="green"
                            onClick={handleExportClick}
                            leftIcon={isExporting ? <Spinner size="sm" /> : <DownloadIcon />}
                            width="50%"
                            isLoading={isExporting}
                            loadingText="Exporting"
                            isDisabled={!isFiltered}
                            fontSize="md"
                            height="42px"
                        >
                            Export Report
                        </Button>
                    </Flex>
                </Flex>
            </Card>

            <Card p={4}>
                <Box overflowX="auto">
                    <Table variant="simple" size="lg">
                        <Thead bg="gray.50">
                            <Tr>
                                <Th fontWeight="bold" fontSize="lg" py={3}>Alert Name</Th>
                                <Th fontWeight="bold" fontSize="lg" py={3}>Alert Status</Th>
                                <Th fontWeight="bold" fontSize="lg" py={3}>Created Time</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {Array.isArray(tArray) && tArray.length > 0 ? (
                                tArray.map((item, index) => (
                                    <Tr
                                        key={index}
                                        _hover={{ bg: 'gray.50' }}
                                        height="48px" // Reduced row height
                                    >
                                        <Td fontSize="lg" py={2}>{item.Label}</Td>
                                        <Td fontSize="lg" py={2}>{item.Status}</Td>
                                        <Td fontSize="lg" py={2}>
                                            {new Date(item.Timestamp).toLocaleString('zh-HK')}
                                        </Td>
                                    </Tr>
                                ))
                            ) : (
                                <Tr>
                                    <Td colSpan={3} textAlign="center" py={8}>
                                        <Text fontSize="lg" color="gray.500">
                                            {isFiltered ? 'No records found for the selected criteria' : 'No records available'}
                                        </Text>
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </Box>

                <Pagination />
            </Card>
        </Box>
    );
}

export default EventLog;